import React, { useEffect, useMemo, useState, useCallback } from "react";
import PageTransition from "../components/PageTransition";
import axios from "axios";
import { BASE_ENDPOINT } from "../helpers/Config";
import toast from "react-hot-toast";
import {
    FaUsers,
    FaUserCheck,
    FaUserSlash,
    FaCoins,
    FaDollarSign,
    FaSyncAlt,
    FaChartPie,
    FaWallet,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const DEFAULT_DATA = {
    users: { total: 0, active: 0, inactive: 0 },
    balance: {
        coin: { total: 0, active: 0, inactive: 0 },
        usdt: { total: 0, active: 0, inactive: 0 },
    },
};

function nfmt(v) {
    const num = Number(v || 0);
    return new Intl.NumberFormat("en-IN").format(num);
}

function pct(part, total) {
    const p = total > 0 ? (Number(part || 0) / Number(total || 0)) * 100 : 0;
    return Math.max(0, Math.min(100, p));
}

function SkeletonLine({ w = "100%" }) {
    return (
        <div className="placeholder-glow">
            <span className="placeholder" style={{ width: w, height: 10, display: "block" }} />
        </div>
    );
}

function KpiTile({ icon, label, value, loading }) {
    return (
        <div className="col-12 col-md-4">
            <div className="card border-0 shadow-sm h-100">
                <div className="card-body d-flex align-items-center gap-3" style={{ minHeight: 92 }}>
                    <div
                        className="rounded-3 d-flex align-items-center justify-content-center"
                        style={{
                            width: 44,
                            height: 44,
                            background: "rgba(13,110,253,0.10)",
                            color: "#0d6efd",
                            flex: "0 0 auto",
                        }}
                    >
                        {icon}
                    </div>
                    <div className="flex-grow-1">
                        <div className="text-muted small">{label}</div>
                        {loading ? (
                            <div className="mt-2">
                                <SkeletonLine w="60%" />
                            </div>
                        ) : (
                            <div className="h5 fw-bold mb-0 mt-1">{value}</div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function Panel({ title, icon, right, children }) {
    return (
        <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-white border-0 pt-3 pb-2">
                <div className="d-flex align-items-center justify-content-between gap-2">
                    <div className="d-flex align-items-center gap-2">
                        <span style={{ color: "#0d6efd" }}>{icon}</span>
                        <div className="fw-semibold">{title}</div>
                    </div>
                    {right}
                </div>
            </div>
            <div className="card-body pt-2">{children}</div>
        </div>
    );
}

function MetricBlock({ title, icon, total, active, inactive, loading, accent = "#0d6efd" }) {
    const activePct = pct(active, total);
    const inactivePct = pct(inactive, total);

    return (
        <div className="border rounded-3 p-3 bg-body-tertiary" style={{ minHeight: 170 }}>
            <div className="d-flex align-items-center justify-content-between mb-2">
                <div className="d-flex align-items-center gap-2">
                    <span style={{ color: accent }}>{icon}</span>
                    <div className="fw-semibold">{title}</div>
                </div>

                {loading ? (
                    <span className="badge text-bg-secondary">Loading</span>
                ) : (
                    <span className="badge text-bg-dark">Total: {nfmt(total)}</span>
                )}
            </div>

            {loading ? (
                <>
                    <SkeletonLine w="70%" />
                    <div className="mt-2" />
                    <SkeletonLine w="55%" />
                    <div className="mt-3" />
                    <SkeletonLine w="100%" />
                </>
            ) : (
                <>
                    <div className="row g-2 mt-1">
                        <div className="col-6">
                            <div className="text-muted small">Active</div>
                            <div className="fw-bold">{nfmt(active)}</div>
                        </div>
                        <div className="col-6">
                            <div className="text-muted small">Inactive</div>
                            <div className="fw-bold">{nfmt(inactive)}</div>
                        </div>
                    </div>

                    <div className="mt-3">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                            <div className="text-muted small">Active ratio</div>
                            <div className="small fw-semibold">{activePct.toFixed(0)}%</div>
                        </div>
                        <div className="progress" style={{ height: 8 }}>
                            <div className="progress-bar" style={{ width: `${activePct}%` }} />
                        </div>
                    </div>

                    <div className="mt-3">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                            <div className="text-muted small">Inactive ratio</div>
                            <div className="small fw-semibold">{inactivePct.toFixed(0)}%</div>
                        </div>
                        <div className="progress" style={{ height: 8 }}>
                            <div className="progress-bar bg-secondary" style={{ width: `${inactivePct}%` }} />
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

export default function Dashboard() {
    const [loading, setLoading] = useState(true);
    const [Data, setData] = useState(DEFAULT_DATA);
    const [lastUpdated, setLastUpdated] = useState(null);

    const navigate = useNavigate();

    const { logout } = useAuth();

    const totals = useMemo(() => {
        const u = Data?.users || {};
        const c = Data?.balance?.coin || {};
        const s = Data?.balance?.usdt || {};

        return {
            usersTotal: Number(u.total || 0),
            usersActive: Number(u.active || 0),
            usersInactive: Number(u.inactive || 0),

            coinTotal: Number(c.total || 0),
            coinActive: Number(c.active || 0),
            coinInactive: Number(c.inactive || 0),

            usdtTotal: Number(s.total || 0),
            usdtActive: Number(s.active || 0),
            usdtInactive: Number(s.inactive || 0),
        };
    }, [Data]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios({
                method: "post",
                url: `${BASE_ENDPOINT}/admin/stats`,
                maxBodyLength: Infinity,
                headers: { "Content-Type": "application/json" },
                data: { token: localStorage.getItem("token") },
            });

            const data = response?.data;

            if (data?.error) {
                if (data?.error == 'session expired') {
                    logout();
                } else {
                    toast.error(data?.error);
                }
            } else {
                setData(data?.data || DEFAULT_DATA);
                setLastUpdated(new Date());
            }
        } catch (error) {
            console.log(error);
            toast.error("Failed to load dashboard stats");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const lastUpdatedText = useMemo(() => {
        if (!lastUpdated) return "—";
        return lastUpdated.toLocaleString("en-IN");
    }, [lastUpdated]);

    return (
        <PageTransition>
            <div className="row g-3">
                {/* Header */}
                <div className="col-12">
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                        <div>
                            <h2 className="fw-bold mb-0">Dashboard</h2>
                            <div className="text-muted">
                                System overview • Last updated: <span className="fw-semibold">{lastUpdatedText}</span>
                            </div>
                        </div>

                        <button
                            className="btn btn-outline-primary d-inline-flex align-items-center gap-2"
                            onClick={fetchData}
                            disabled={loading}
                            type="button"
                        >
                            <FaSyncAlt />
                            {loading ? "Refreshing..." : "Refresh"}
                        </button>
                    </div>
                </div>

                {/* KPI Strip (consistent height tiles) */}
                <KpiTile
                    loading={loading}
                    icon={<FaUsers />}
                    label="Total Users"
                    value={nfmt(totals.usersTotal)}
                />
                <KpiTile
                    loading={loading}
                    icon={<FaCoins />}
                    label="Coin Total"
                    value={nfmt(totals.coinTotal)}
                />
                <KpiTile
                    loading={loading}
                    icon={<FaDollarSign />}
                    label="USDT Total"
                    value={nfmt(totals.usdtTotal)}
                />

                {/* Main Panels */}
                <div className="col-12 col-lg-6">
                    <Panel
                        title="Users"
                        icon={<FaChartPie />}
                        right={
                            loading ? (
                                <span className="badge text-bg-secondary">Loading</span>
                            ) : (
                                <span className="badge text-bg-primary">
                                    Active: {nfmt(totals.usersActive)} • Inactive: {nfmt(totals.usersInactive)}
                                </span>
                            )
                        }
                    >
                        {/* Uniform block height keeps UI clean */}
                        <MetricBlock
                            title="User Status"
                            icon={<FaUsers />}
                            total={totals.usersTotal}
                            active={totals.usersActive}
                            inactive={totals.usersInactive}
                            loading={loading}
                            accent="#0d6efd"
                        />

                        <div className="mt-3 border rounded-3 p-3 bg-white">
                            <div className="fw-semibold mb-2 d-flex align-items-center gap-2">
                                <span style={{ color: "#198754" }}>
                                    <FaUserCheck />
                                </span>
                                Quick Breakdown
                            </div>

                            {loading ? (
                                <>
                                    <SkeletonLine w="90%" />
                                    <div className="mt-2" />
                                    <SkeletonLine w="70%" />
                                </>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-sm align-middle mb-0">
                                        <thead>
                                            <tr className="text-muted">
                                                <th>Type</th>
                                                <th className="text-end">Count</th>
                                                <th className="text-end">Share</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td className="fw-semibold">
                                                    <span className="me-2" style={{ color: "#198754" }}>
                                                        <FaUserCheck />
                                                    </span>
                                                    Active
                                                </td>
                                                <td className="text-end">{nfmt(totals.usersActive)}</td>
                                                <td className="text-end">{pct(totals.usersActive, totals.usersTotal).toFixed(0)}%</td>
                                            </tr>
                                            <tr>
                                                <td className="fw-semibold">
                                                    <span className="me-2" style={{ color: "#dc3545" }}>
                                                        <FaUserSlash />
                                                    </span>
                                                    Inactive
                                                </td>
                                                <td className="text-end">{nfmt(totals.usersInactive)}</td>
                                                <td className="text-end">{pct(totals.usersInactive, totals.usersTotal).toFixed(0)}%</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </Panel>
                </div>

                <div className="col-12 col-lg-6">
                    <Panel
                        title="Balances"
                        icon={<FaWallet />}
                        right={
                            loading ? (
                                <span className="badge text-bg-secondary">Loading</span>
                            ) : (
                                <span className="badge text-bg-dark">
                                    Coin: {nfmt(totals.coinTotal)} • USDT: {nfmt(totals.usdtTotal)}
                                </span>
                            )
                        }
                    >
                        {/* Two identical blocks = perfectly balanced UI */}
                        <div className="row g-3">
                            <div className="col-12">
                                <MetricBlock
                                    title="Coin"
                                    icon={<FaCoins />}
                                    total={totals.coinTotal}
                                    active={totals.coinActive}
                                    inactive={totals.coinInactive}
                                    loading={loading}
                                    accent="#0d6efd"
                                />
                            </div>

                            <div className="col-12">
                                <MetricBlock
                                    title="USDT"
                                    icon={<FaDollarSign />}
                                    total={totals.usdtTotal}
                                    active={totals.usdtActive}
                                    inactive={totals.usdtInactive}
                                    loading={loading}
                                    accent="#20c997"
                                />
                            </div>
                        </div>
                    </Panel>
                </div>
            </div>
        </PageTransition>
    );
}
